import { useCallback, useEffect, useMemo, useState } from "react";

import { Card, CardBody } from "reactstrap";

import { Input, Select, Checkbox, FormFooterActions } from "@atoms";
import { msgs, statusOptions } from "@config/conf";
import {
  sanitizeValue,
  validateMaxValue,
  validateNumberField,
  splitStringyRate,
  formatToPercentage,
} from "@containers/components/helpers";
import FormSpinner from "@containers/FormSpinner/FormSpinner";
import useApiFetch from "@hooks/useApiFetch";
import useQueryParams from "@hooks/useQueryParams";

const { fieldError } = msgs || {};

const queryPath = ["from", "sortProductName", "sortCarrierName", "pageSize"];

const ProductFactorForm = (props) => {
  const {
    form,
    productID = "",
    productFactorID = "",
    loadingResponse,
  } = props || {};

  const { get, put, post, isPending } = useApiFetch();
  const [productData, setProductData] = useState({});
  const { getQueryParams, buildQueryParams } = useQueryParams();

  const { from: page, ...restParams } = getQueryParams(queryPath);

  const { control, handleSubmit, setValue, watch } = form || {};

  const isRequesting = loadingResponse || isPending;
  const { joint, jointPercent } = watch();
  const colaVal = watch("cola");
  const getProduct = useCallback(async () => {
    const response = await get(`products/${productID}`);
    setProductData(response);
  }, []);

  useEffect(() => {
    getProduct();
  }, [getProduct]);

  useEffect(() => {
    if (productData?.productID) {
      setValue("productName", productData?.productName);
    }
    setValue("jointPercent", joint ? jointPercent : "0");
  }, [productData?.productID, joint]);

  useEffect(() => {
    if (
      productFactorID &&
      colaVal != null &&
      colaVal !== "" &&
      !String(colaVal).includes("%")
    ) {
      setValue("cola", formatToPercentage(Number(colaVal) * 100));
    }
  }, [productFactorID, colaVal]);

  const goBackUrl = useMemo(() => {
    const queryParams = buildQueryParams({ page, ...restParams });

    return `/products?${
      productFactorID ? `id=${productID}&` : ""
    }${queryParams}`;
  }, [page, restParams]);

  const onSave = useCallback((values) => {
    const {
      jointPercent,
      deferralYears,
      factorName,
      factorID,
      refundable,
      isLifetime,
      joint,
      selectable,
      status,
      factorDescription,
      factorHelper,
      periodCertainYears,
      cola,
      sortOrder,
      qlacIncomeStartAge,
    } = values || {};

    const payloadValues = {
      deferralYears: Number(deferralYears),
      jointPercent: Number(jointPercent),
      factorName: sanitizeValue(factorName),
      factorID: factorID ? sanitizeValue(factorID) : undefined,
      productID,
      refundable,
      isLifetime,
      joint,
      selectable,
      status,
      factorDescription: sanitizeValue(factorDescription),
      factorHelper: sanitizeValue(factorHelper),
      periodCertainYears: periodCertainYears
        ? Number(periodCertainYears)
        : null,
      qlacIncomeStartAge: qlacIncomeStartAge
        ? Number(qlacIncomeStartAge)
        : null,
      cola:
        cola === null || cola === undefined || cola === ""
          ? null
          : typeof cola === "number"
            ? cola
            : splitStringyRate(cola),

      sortOrder: sortOrder ? Number(sortOrder) : null,
    };

    factorID
      ? put(`products/productFactor/${productFactorID}`, payloadValues, {
          redirectUrl: goBackUrl,
        })
      : post("products/productfactors", payloadValues, {
          redirectUrl: goBackUrl,
        });
  }, []);

  const commonProps = useMemo(
    () => ({
      control,
      rules: {
        required: fieldError,
      },
    }),
    [control]
  );

  return (
    <Card className="product_factor_form_wrapper">
      <FormSpinner requesting={isRequesting} />
      <CardBody className="form form--horizontal product_page_wrapper">
        <Input
          {...commonProps}
          name="productName"
          label="Product Name"
          required
          isReadOnly
        />
        <Input
          {...commonProps}
          name="factorName"
          label="Factor Name"
          required
        />
        <Input
          control={control}
          name="factorDescription"
          label="Factor Description"
        />
        <Input
          control={control}
          name="factorHelper"
          label="Factor Helper Message"
        />
        <Checkbox control={control} name="joint" label="Joint" />
        <Input
          control={control}
          rules={{
            validate: (value) => validateMaxValue(value),
          }}
          name="jointPercent"
          label="Joint Percent"
          isReadOnly={!joint}
          formatter={{ numericOnly: true }}
        />
        <Checkbox control={control} name="refundable" label="Refundable" />
        <Checkbox control={control} name="isLifetime" label="Is Lifetime" />
        <Input
          control={control}
          rules={{
            required: fieldError,
            validate: (value) => validateNumberField(value, fieldError),
          }}
          name="deferralYears"
          label="Deferral Years"
          required
          formatter={{ numericOnly: true }}
        />

        <Input
          control={control}
          rules={{
            validate: (value) => validateNumberField(value),
          }}
          name="qlacIncomeStartAge"
          label="Income Start Age"
          formatter={{ numericOnly: true }}
        />

        <Input
          control={control}
          name="periodCertainYears"
          label="Period Certain Years"
          formatter={{ numericOnly: true }}
          maxLength={2}
        />
        <Input
          control={control}
          name="cola"
          label="COLA"
          rules={{
            validate: (value) => {
              if (!value && value !== 0) return true; // allow empty
              return validateMaxValue(value); // validate only if entered
            },
          }}
          formatter={{ numericOnly: true, shouldPercentage: true }}
          maxLength={10}
        />
        <Checkbox control={control} name="selectable" label="Selectable" />
        <Input
          control={control}
          name="sortOrder"
          label="Sort Order"
          formatter={{ numericOnly: true }}
          maxLength={2}
        />
        <Select
          {...commonProps}
          name="status"
          label="Status"
          required
          options={statusOptions}
        />
        {productFactorID && (
          <Input
            control={control}
            name="lastUpdatedOn"
            label="Last Updated"
            isReadOnly
          />
        )}
        <FormFooterActions
          linkProps={[{ path: goBackUrl }]}
          buttonProps={[{ onClick: handleSubmit(onSave) }]}
        />
      </CardBody>
    </Card>
  );
};

export default ProductFactorForm;
